import { getAcquisitionProperties } from '@/lib/acquisition';
import { posthog } from 'posthog-js';
import { Button } from '@/components/ui/button';
import { useHasActivePlan, type CtaState } from '@/hooks/useHasActivePlan';
import { getHomepageAuthDestination } from '@/lib/homepageAuthRedirect';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface StartFreeButtonProps {
    onClick?: () => void;
    size?: 'default' | 'sm' | 'lg';
    className?: string;
    showArrow?: boolean;
    variant?: 'default' | 'primary' | 'mobile';
    useHomepageAuthDestination?: boolean;
}

const ctaConfig: Record<CtaState, { label: string; destination: string }> = {
    create_book: { label: 'Create Book', destination: '/create-book' },
    view_pricing: { label: 'See plans & pricing', destination: '/pricing' },
};

export function StartFreeButton({
    onClick,
    size = 'default',
    className = '',
    showArrow = false,
    variant = 'default',
    useHomepageAuthDestination = false,
}: StartFreeButtonProps) {
    const navigate = useNavigate();
    const { ctaState } = useHasActivePlan();
    const { label, destination } = ctaConfig[ctaState];

    const handleClick = async () => {
        if (onClick) {
            onClick();
        }

        const nextDestination =
            useHomepageAuthDestination && ctaState === 'view_pricing'
                ? await getHomepageAuthDestination()
                : destination;

        posthog.capture('plan_cta_clicked', { ...getAcquisitionProperties(), page_path: window.location.pathname, destination: nextDestination, cta_state: ctaState });
        navigate(nextDestination);
    };

    if (variant === 'mobile') {
        return (
            <Button
                className={`w-full rounded-full bg-gradient-to-r from-primary via-secondary to-primary-dark py-2.5 text-sm font-semibold text-white shadow-sm shadow-primary/30 ${className}`}
                onClick={handleClick}
            >
                {label}
            </Button>
        );
    }

    if (variant === 'primary') {
        return (
            <Button
                size={size}
                className={`rounded-full bg-slate-950 px-7 ${className}`}
                onClick={handleClick}
            >
                {label}
                {showArrow && <ArrowRight className="ml-2 h-4 w-4" />}
            </Button>
        );
    }

    return (
        <Button
            size={size}
            className={className}
            onClick={handleClick}
        >
            {label}
            {showArrow && <ArrowRight className="ml-2 h-4 w-4" />}
        </Button>
    );
}
